import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import { adminAuditService } from '@/services/admin-audit.service'
import { isErrorResponse, type ErrorResponse } from '@/types/api-response'
import type { AuditLogListFilters } from '@/types/admin'

/**
 * Query keys for audit log-related queries
 */
export const auditKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditKeys.all, 'list'] as const,
  list: (filters: AuditLogListFilters) => [...auditKeys.lists(), filters] as const,
  filters: () => [...auditKeys.all, 'filters'] as const,
  details: () => [...auditKeys.all, 'detail'] as const,
  detail: (id: string) => [...auditKeys.details(), id] as const,
}

/**
 * Hook to fetch one page of audit logs.
 * Returns: { data: AuditLogListItem[]; pagination: PaginationInfo }
 */
export function useAuditLogs(filters: AuditLogListFilters) {
  return useQuery({
    queryKey: auditKeys.list(filters),
    queryFn: () => adminAuditService.getAuditLogs(filters),
    staleTime: 10000, // 10 seconds

    // Keeps the previous page on screen while the next one loads, so paging and filtering do not
    // blank the table out from under the operator.
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch the values the filter dropdowns offer.
 * Returns: AuditLogFacets
 */
export function useAuditLogFilters() {
  return useQuery({
    queryKey: auditKeys.filters(),
    queryFn: () => adminAuditService.getAuditLogFilters(),

    // The server caches these for five minutes; matching that here keeps the dropdowns off the
    // wire for the whole time an operator spends on the screen.
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch the full row behind a list entry.
 * Returns: AuditLogDetail
 */
export function useAuditLogDetail(id: string | undefined) {
  return useQuery({
    queryKey: auditKeys.detail(id ?? ''),
    queryFn: () => adminAuditService.getAuditLogById(id!),
    enabled: !!id,
  })
}

/**
 * `exportAuditLogs` requests `responseType: 'blob'`, and axios honours that responseType for an
 * error response too — a 403/500 body arrives as a `Blob` rather than parsed JSON, so the
 * interceptor in `lib/api.ts` never sees the `ErrorResponse` shape it looks for and rejects with
 * the raw `AxiosError` instead. Its `.message` is then axios's own transport line ("Request
 * failed with status code 403"), not the API's sentence. Read the Blob back out here so the toast
 * carries the same wording the JSON export list already relies on.
 */
async function resolveExportErrorMessage(error: unknown): Promise<string> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text()
      const parsed: unknown = JSON.parse(text)
      if (isErrorResponse(parsed) && (parsed as ErrorResponse).error.message) {
        return (parsed as ErrorResponse).error.message
      }
    } catch {
      // Not JSON, or an empty body — fall through to the generic message below.
    }
  }

  // An error with an empty message would otherwise raise an empty toast.
  return (error instanceof Error && error.message) || 'Failed to export audit logs'
}

/**
 * Hook to export audit logs
 * Service returns: Blob (file download)
 */
export function useExportAuditLogs() {
  return useMutation({
    mutationFn: ({
      format,
      filters,
    }: {
      format: 'csv' | 'json'
      filters: AuditLogListFilters
    }) => adminAuditService.exportAuditLogs(format, filters),
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${new Date().toISOString()}.${variables.format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success(`Audit logs exported as ${variables.format.toUpperCase()}`)
    },
    onError: async (error: Error) => {
      toast.error(await resolveExportErrorMessage(error))
    },
  })
}
